import { Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet, useParams } from "react-router-dom";
import { AppLayout } from "@/shared/components/AppLayout";
import { ProtectedRoute } from "@/modules/auth/components/ProtectedRoute";
import { AttendanceGuardModal } from "@/modules/attendance";
import { SessionStartGate } from "@/modules/timetracking";
import { RequirePermissionRoute } from "@/modules/auth/components/RequirePermissionRoute";
import { RoleLandingRedirect } from "@/modules/auth/components/RoleLandingRedirect";
import { RouteErrorBoundary } from "@/shared/components/RouteErrorBoundary";
import { MoneyPrecisionScope } from "@/shared/components/MoneyPrecisionScope";
import { lazyWithRetry } from "@/shared/utils/lazyWithRetry";

const LoginPage = lazyWithRetry(() => import("@/modules/auth/pages/LoginPage"));
const ForgotPasswordPage = lazyWithRetry(() => import("@/modules/auth/pages/ForgotPasswordPage"));
const ForgotPasswordOtpPage = lazyWithRetry(() => import("@/modules/auth/pages/ForgotPasswordOtpPage"));
const ResetPasswordPage = lazyWithRetry(() => import("@/modules/auth/pages/ResetPasswordPage"));
const DashboardPage = lazyWithRetry(() => import("@/modules/dashboard-v2/pages/DashboardPage"));
const InsightsPage = lazyWithRetry(() => import("@/modules/dashboard-v2/pages/InsightsPage"));
const UsersListPage = lazyWithRetry(() => import("@/modules/users/pages/UsersListPage"));
const DepartmentsListPage = lazyWithRetry(() => import("@/modules/departments/pages/DepartmentsListPage"));
const ClientsListPage = lazyWithRetry(() => import("@/modules/clients/pages/ClientsListPage"));
const ClientDetailPage = lazyWithRetry(() => import("@/modules/clients/pages/ClientDetailPage"));
const ClientGroupDetailPage = lazyWithRetry(() => import("@/modules/clients/pages/ClientGroupDetailPage"));
const TemplatesListPage = lazyWithRetry(() => import("@/modules/templates/pages/TemplatesListPage"));
const TemplateCreatePage = lazyWithRetry(() => import("@/modules/templates/pages/TemplateCreatePage"));
const TemplateEditorPage = lazyWithRetry(() => import("@/modules/templates/pages/TemplateEditorPage"));
const ProjectsListPage = lazyWithRetry(() => import("@/modules/projects/pages/ProjectsListPage"));
const ProjectDetailPage = lazyWithRetry(() => import("@/modules/projects/pages/ProjectDetailPage"));
const WorkUnitsListPage = lazyWithRetry(() => import("@/modules/workunits/pages/WorkUnitsListPage"));
const WorkUnitDetailPage = lazyWithRetry(() => import("@/modules/workunits/pages/WorkUnitDetailPage"));
const DocumentRegistryPage = lazyWithRetry(() => import("@/modules/documents/pages/DocumentRegistryPage"));
const ApprovalsQueuePage = lazyWithRetry(() => import("@/modules/approvals/pages/ApprovalsQueuePage"));
const DepartmentDetailPage = lazyWithRetry(() => import("@/modules/departments/pages/DepartmentDetailPage"));
const AttendancePage = lazyWithRetry(() => import("@/modules/dashboard-v2/pages/AttendancePage"));
const EmployeeAttendanceDetailPage = lazyWithRetry(
  () => import("@/modules/attendance/pages/EmployeeAttendanceDetailPage"),
);
const LocationsListPage = lazyWithRetry(() => import("@/modules/locations/pages/LocationsListPage"));
const HolidaysPage = lazyWithRetry(() => import("@/modules/holidays/pages/HolidaysPage"));
/** /payroll/runs/:runId/payments -> /payroll/payments?run=:runId */
function RunPaymentsRedirect() {
  const { runId } = useParams<{ runId: string }>();
  return <Navigate to={`/payroll/payments${runId ? `?run=${runId}` : ""}`} replace />;
}

const PayrollEmployeesListPage = lazyWithRetry(
  () => import("@/modules/payroll/pages/EmployeesListPage"),
);
const PayrollRunsListPage = lazyWithRetry(() => import("@/modules/payroll/pages/RunsListPage"));
const PayrollRunDetailPage = lazyWithRetry(() => import("@/modules/payroll/pages/RunDetailPage"));
const PayrollLineBreakdownPage = lazyWithRetry(
  () => import("@/modules/payroll/pages/LineBreakdownPage"),
);
const PayrollPaymentsPage = lazyWithRetry(
  () => import("@/modules/payroll/pages/PaymentsPage"),
);
const PayrollEmployeeDetailPage = lazyWithRetry(
  () => import("@/modules/payroll/pages/EmployeeDetailPage"),
);
const MyPayPage = lazyWithRetry(() => import("@/modules/payroll/pages/MyPayPage"));
const AdvancesPage = lazyWithRetry(() => import("@/modules/payroll/pages/AdvancesPage"));
const CashPoolsPage = lazyWithRetry(
  () => import("@/modules/payroll/pages/CashPoolsPage"),
);
const CashPoolDetailPage = lazyWithRetry(
  () => import("@/modules/payroll/pages/CashPoolDetailPage"),
);
const ReimbursementsPage = lazyWithRetry(() => import("@/modules/payroll/pages/ReimbursementsPage"));
const ClaimApprovalsPage = lazyWithRetry(() => import("@/modules/payroll/pages/ClaimApprovalsPage"));
const ApprovalChainConfigPage = lazyWithRetry(
  () => import("@/modules/payroll/pages/ApprovalChainConfigPage"),
);
const PayrollSettingsPage = lazyWithRetry(
  () => import("@/modules/payroll/pages/PayrollSettingsPage"),
);
const PayrollGuidePage = lazyWithRetry(
  () => import("@/modules/payroll/pages/PayrollGuidePage"),
);
const PayrollLayout = lazyWithRetry(() => import("@/modules/payroll/PayrollLayout"));
const DeactivationReasonsPage = lazyWithRetry(
  () => import("@/modules/workunits/pages/DeactivationReasonsPage"),
);
const LeavesListPage = lazyWithRetry(() => import("@/modules/leaves/pages/LeavesListPage"));
const LeaveTypesListPage = lazyWithRetry(
  () => import("@/modules/leaveTypes/pages/LeaveTypesListPage"),
);
const LiveMonitorPage = lazyWithRetry(() => import("@/modules/dashboard-v2/pages/LiveMonitorPage"));
const LiveMonitorUserDetailPage = lazyWithRetry(() => import("@/modules/dashboard-v2/pages/LiveMonitorDetail"));
const NotificationsPage = lazyWithRetry(() => import("@/modules/notifications/pages/NotificationsPage"));
const MailLayout = lazyWithRetry(() => import("@/modules/mail/MailLayout"));
const MailInboxPage = lazyWithRetry(() => import("@/modules/mail/pages/InboxPage"));
const MailboxesPage = lazyWithRetry(() => import("@/modules/mail/pages/MailboxesPage"));
const MailRulesPage = lazyWithRetry(() => import("@/modules/mail/pages/RulesPage"));
const ErrorLogPage = lazyWithRetry(() => import("@/modules/errorLog/pages/ErrorLogPage"));
const AIRunsListPage = lazyWithRetry(() => import("@/modules/aiSteps/pages/AIRunsListPage"));
const RegularisationsPage = lazyWithRetry(() => import("@/modules/regularisations/pages/RegularisationsPage"));
const MessagingConfigPage = lazyWithRetry(() => import("@/modules/messaging/pages/MessagingConfigPage"));
const ReportsPage = lazyWithRetry(() => import("@/modules/reports/pages/ReportsPage"));
const ReportViewerPage = lazyWithRetry(() => import("@/modules/reports/pages/ReportViewerPage"));
const ClientDocsQueuePage = lazyWithRetry(() => import("@/modules/clientportal/pages/ClientDocsQueuePage"));
const ClientPortalPage = lazyWithRetry(() => import("@/modules/clientportal/pages/ClientPortalPage"));
const ClientMembersPage = lazyWithRetry(() => import("@/modules/clientportal/pages/ClientMembersPage"));

const Loading = () => <div className="p-6 text-sm text-gray-500">Loading...</div>;

export const router = createBrowserRouter([
  {
    path: "/login",
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<Loading />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: "/forgot-password",
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<Loading />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    path: "/forgot-password-otp",
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<Loading />}>
        <ForgotPasswordOtpPage />
      </Suspense>
    ),
  },
  {
    path: "/reset-password",
    errorElement: <RouteErrorBoundary />,
    element: (
      <Suspense fallback={<Loading />}>
        <ResetPasswordPage />
      </Suspense>
    ),
  },
  {
    // The whole Mail hub in its own browser tab, without the app menu (Mail ›
    // "Open in New Tab"), with a full-screen button. Same pages, permissions
    // and privacy rules as /mail.
    path: "/mail-window",
    errorElement: <RouteErrorBoundary />,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<Loading />}>
          <RequirePermissionRoute permission={["mail.use", "mail.manage"]}>
            <MailLayout standalone />
            {/* Same gates as the app shell (AppLayout): the pop-out window must
                not become a way round mandatory attendance / session start. */}
            <AttendanceGuardModal />
            <SessionStartGate />
          </RequirePermissionRoute>
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="inbox" replace /> },
      {
        path: "inbox",
        element: (
          <RequirePermissionRoute permission="mail.use">
            <MailInboxPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "rules",
        element: (
          <RequirePermissionRoute permission="mail.use">
            <MailRulesPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "mailboxes",
        element: (
          <RequirePermissionRoute permission="mail.manage">
            <MailboxesPage />
          </RequirePermissionRoute>
        ),
      },
    ],
  },
  {
    path: "/",
    errorElement: <RouteErrorBoundary />,
    element: (
      <ProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </AppLayout>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <RoleLandingRedirect /> },
      { path: "dashboard", element: <DashboardPage /> },
      {
        path: "insights",
        element: (
          <RequirePermissionRoute permission="reports.view">
            <InsightsPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "users",
        element: (
          <RequirePermissionRoute permission="users.read">
            <UsersListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "departments",
        element: (
          <RequirePermissionRoute permission="departments.read">
            <DepartmentsListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "clients",
        element: (
          <RequirePermissionRoute permission="clients.read">
            <ClientsListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "clients/:id",
        element: (
          <RequirePermissionRoute permission="clients.read">
            <ClientDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "client-groups/:id",
        element: (
          <RequirePermissionRoute permission="clientGroups.read">
            <ClientGroupDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "templates",
        element: (
          <RequirePermissionRoute permission="templates.read">
            <TemplatesListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "templates/new",
        element: (
          <RequirePermissionRoute permission="templates.create">
            <TemplateCreatePage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "templates/:templateId",
        element: (
          <RequirePermissionRoute permission="templates.update">
            <TemplateEditorPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "projects",
        element: (
          <RequirePermissionRoute permission="projects.read">
            <ProjectsListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "projects/:projectId",
        element: (
          <RequirePermissionRoute permission="projects.read">
            <ProjectDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "work-units",
        element: (
          <RequirePermissionRoute permission="workunits.read">
            <WorkUnitsListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "work-units/:id",
        element: (
          <RequirePermissionRoute permission="workunits.read">
            <WorkUnitDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "documents",
        element: (
          <RequirePermissionRoute permission="documents.registry.read">
            <DocumentRegistryPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "approvals",
        element: (
          <RequirePermissionRoute
            permission={[
              "approvals.resolve",
              "workunits.request_deactivation",
              "workunits.deactivate",
            ]}
          >
            <ApprovalsQueuePage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "attendance",
        element: (
          <RequirePermissionRoute
            permission={["attendance.mark", "attendance.read_self", "attendance.read_all"]}
          >
            <AttendancePage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "attendance/users/:userId",
        element: (
          <RequirePermissionRoute permission="attendance.read_all">
            <EmployeeAttendanceDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "locations",
        element: (
          <RequirePermissionRoute permission="locations.read">
            <LocationsListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        // Legacy path — holidays now live inside the Payroll hub.
        path: "holidays",
        element: <Navigate to="/payroll/holidays" replace />,
      },
      {
        // Payroll hub: ONE sidebar entry, tabbed sections (owner feedback —
        // no scattered per-action entries).
        path: "payroll",
        element: (
          <RequirePermissionRoute
            permission={[
              // `holidays.read` intentionally absent — Operations holds it
              // (API read) but has no payroll UI; including it made the hub
              // reachable with every inner tab blocked.
              "payroll.read",
              "payroll.approve_hr",
              "payroll.approve_final",
            ]}
          >
            <PayrollLayout />
          </RequirePermissionRoute>
        ),
        children: [
          { index: true, element: <Navigate to="runs" replace /> },
          {
            path: "runs",
            element: (
              <RequirePermissionRoute permission="payroll.read">
                <PayrollRunsListPage />
              </RequirePermissionRoute>
            ),
          },
          {
            path: "runs/:runId",
            element: (
              <RequirePermissionRoute permission="payroll.read">
                <PayrollRunDetailPage />
              </RequirePermissionRoute>
            ),
          },
          {
            path: "runs/:runId/lines/:lineId",
            element: (
              <RequirePermissionRoute permission="payroll.view_breakdown">
                <PayrollLineBreakdownPage />
              </RequirePermissionRoute>
            ),
          },
          {
            // Intermediate breadcrumb target — no list here, bounce to the run.
            path: "runs/:runId/lines",
            element: <Navigate to=".." relative="path" replace />,
          },
          {
            // Legacy run-scoped payments page — payments are now recorded
            // against the employee's ledger balance on the Payments tab.
            path: "runs/:runId/payments",
            element: <RunPaymentsRedirect />,
          },
          {
            path: "payments",
            element: (
              <RequirePermissionRoute permission="payroll.read">
                <PayrollPaymentsPage />
              </RequirePermissionRoute>
            ),
          },
          {
            path: "employees",
            element: (
              <RequirePermissionRoute permission="payroll.read">
                <PayrollEmployeesListPage />
              </RequirePermissionRoute>
            ),
          },
          {
            path: "employees/:employeeId",
            element: (
              <RequirePermissionRoute permission="payroll.read">
                <PayrollEmployeeDetailPage />
              </RequirePermissionRoute>
            ),
          },
          {
            path: "advances",
            element: (
              <RequirePermissionRoute
                permission={["payroll.approve_hr", "payroll.approve_final"]}
              >
                <AdvancesPage />
              </RequirePermissionRoute>
            ),
          },
          {
            // Retired tab (cash excess is a negative ledger balance now).
            path: "carry-forwards",
            element: <Navigate to="/payroll/payments?state=recoverable" replace />,
          },
          {
            path: "cash-pools",
            element: (
              <RequirePermissionRoute permission="payroll.read">
                <CashPoolsPage />
              </RequirePermissionRoute>
            ),
          },
          {
            path: "cash-pools/:poolId",
            element: (
              <RequirePermissionRoute permission="payroll.read">
                <CashPoolDetailPage />
              </RequirePermissionRoute>
            ),
          },
          {
            path: "reimbursements",
            element: (
              <RequirePermissionRoute permission="payroll.read">
                <ReimbursementsPage />
              </RequirePermissionRoute>
            ),
          },
          {
            path: "holidays",
            element: (
              <RequirePermissionRoute permission="holidays.read">
                <HolidaysPage />
              </RequirePermissionRoute>
            ),
          },
          {
            path: "approval-chain",
            element: (
              <RequirePermissionRoute permission="payroll.manage_approval_chain">
                <ApprovalChainConfigPage />
              </RequirePermissionRoute>
            ),
          },
          {
            path: "settings",
            element: (
              <RequirePermissionRoute permission="payroll.settings_write">
                <PayrollSettingsPage />
              </RequirePermissionRoute>
            ),
          },
          {
            path: "guide",
            element: (
              <RequirePermissionRoute permission="payroll.view_guide">
                <PayrollGuidePage />
              </RequirePermissionRoute>
            ),
          },
        ],
      },
      {
        path: "my-pay",
        element: (
          <RequirePermissionRoute permission="payroll.self_service">
            <MoneyPrecisionScope>
              <MyPayPage />
            </MoneyPrecisionScope>
          </RequirePermissionRoute>
        ),
      },
      {
        path: "claim-approvals",
        element: (
          <RequirePermissionRoute permission="payroll.self_service">
            <MoneyPrecisionScope>
              <ClaimApprovalsPage />
            </MoneyPrecisionScope>
          </RequirePermissionRoute>
        ),
      },
      {
        path: "leave-types",
        element: (
          <RequirePermissionRoute permission="leaveTypes.manage">
            <LeaveTypesListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "deactivation-reasons",
        element: (
          <RequirePermissionRoute permission="deactivation_reasons.manage">
            <DeactivationReasonsPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "leaves",
        element: (
          <RequirePermissionRoute permission={["leaves.read_self", "leaves.read_all"]}>
            <LeavesListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "live-monitor",
        element: (
          <RequirePermissionRoute permission="liveMonitor.view">
            <LiveMonitorPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "live-monitor/users/:userId",
        element: (
          <RequirePermissionRoute permission="liveMonitor.view">
            <LiveMonitorUserDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "notifications",
        element: (
          <RequirePermissionRoute permission="notifications.read_self">
            <NotificationsPage />
          </RequirePermissionRoute>
        ),
      },
      {
        // Mail hub (docs/plans/mail-hub-design.md): ONE sidebar entry, tabs
        // inside. Inbox = mail.use; Mailboxes admin = mail.manage (SA / TA).
        path: "mail",
        element: (
          <RequirePermissionRoute permission={["mail.use", "mail.manage"]}>
            <MailLayout />
          </RequirePermissionRoute>
        ),
        children: [
          { index: true, element: <Navigate to="inbox" replace /> },
          {
            path: "inbox",
            element: (
              <RequirePermissionRoute permission="mail.use">
                <MailInboxPage />
              </RequirePermissionRoute>
            ),
          },
          {
            path: "rules",
            element: (
              <RequirePermissionRoute permission="mail.use">
                <MailRulesPage />
              </RequirePermissionRoute>
            ),
          },
          {
            path: "mailboxes",
            element: (
              <RequirePermissionRoute permission="mail.manage">
                <MailboxesPage />
              </RequirePermissionRoute>
            ),
          },
        ],
      },
      {
        path: "error-log",
        element: (
          <RequirePermissionRoute permission="errorLog.view">
            <ErrorLogPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "ai-runs",
        element: (
          <RequirePermissionRoute permission="aiRuns.view">
            <AIRunsListPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "regularisations",
        element: <RegularisationsPage />,
      },
      {
        path: "reports",
        element: (
          <RequirePermissionRoute permission="reports.view">
            <ReportsPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "reports/:reportId",
        element: (
          <RequirePermissionRoute permission="reports.view">
            <ReportViewerPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "messaging",
        element: (
          <RequirePermissionRoute permission="messaging.manage">
            <MessagingConfigPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "departments/:id",
        element: (
          <RequirePermissionRoute permission="departments.read">
            <DepartmentDetailPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "client-docs",
        element: (
          <RequirePermissionRoute permission="clientdocs.list.global">
            <ClientDocsQueuePage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "portal",
        element: (
          <RequirePermissionRoute permission="clientdocs.upload">
            <ClientPortalPage />
          </RequirePermissionRoute>
        ),
      },
      {
        path: "client-members",
        element: (
          <RequirePermissionRoute permission="clientdocs.upload">
            <ClientMembersPage />
          </RequirePermissionRoute>
        ),
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);
